import type { AppData, DeletableModule } from "@/types";
import { formatCOP } from "@/utils/format";

export interface DeleteItem {
  id: string;
  title: string;
  subtitle: string;
  meta?: string;
}

export interface DeleteModule {
  key: DeletableModule;
  label: string;
  icon: string;
  hint: string;
  items: DeleteItem[];
}

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
};

// Construye la lista de módulos con sus registros actuales para el borrado selectivo.
export const buildDeleteModules = (data: AppData): DeleteModule[] => {
  return [
    {
      key: "products",
      label: "Catálogo",
      icon: "ri-price-tag-3-line",
      hint: "Productos del catálogo. Al borrar uno también se elimina su registro de inventario.",
      items: data.products.map((p) => ({
        id: p.id,
        title: p.name,
        subtitle: `SKU ${p.sku} · ${p.category}`,
        meta: formatCOP(p.pricePublic),
      })),
    },
    {
      key: "inventory",
      label: "Inventario",
      icon: "ri-archive-2-line",
      hint: "Registros de inventario por producto (casa, punto clear y consignación). Al borrar un registro se pone su stock en cero; el producto del catálogo se conserva.",
      items: data.inventory.map((inv) => {
        const product = data.products.find((p) => p.id === inv.productId);
        const consignment = Object.values(inv.stockByAdvisor ?? {}).reduce(
          (acc, n) => acc + (n ?? 0),
          0
        );
        const owned = Object.values(inv.stockOwnedByAdvisor ?? {}).reduce(
          (acc, n) => acc + (n ?? 0),
          0
        );
        const total = inv.stockCasa + inv.stockPuntoClear + consignment + owned;
        return {
          id: inv.productId,
          title: product?.name ?? "Producto sin catálogo",
          subtitle: `Casa ${inv.stockCasa} · Clear ${inv.stockPuntoClear} · Consig. ${consignment} · Asesor ${owned}`,
          meta: `${total} und`,
        };
      }),
    },
    {
      key: "clients",
      label: "Clientes",
      icon: "ri-user-line",
      hint: "Clientes registrados. Sus ventas ya hechas se conservan en el historial.",
      items: data.clients.map((c) => ({
        id: c.id,
        title: c.name,
        subtitle: c.cedula ? `C.C. ${c.cedula}` : "Sin documento",
        meta: c.phone || "",
      })),
    },
    {
      key: "sales",
      label: "Ventas",
      icon: "ri-shopping-bag-3-line",
      hint: "Ventas registradas. Afectan reportes, utilidades y puntos.",
      items: data.sales.map((s) => ({
        id: s.id,
        title: s.productName,
        subtitle: `${s.clientName} · ${fmtDate(s.date)} · ${s.qty} und`,
        meta: formatCOP(s.total),
      })),
    },
    {
      key: "expenses",
      label: "Gastos",
      icon: "ri-wallet-3-line",
      hint: "Gastos y salidas de dinero registradas por billetera.",
      items: data.expenses.map((e) => ({
        id: e.id,
        title: e.concept,
        subtitle: `${e.category} · ${e.wallet} · ${fmtDate(e.date)}`,
        meta: formatCOP(e.amount),
      })),
    },
    {
      key: "receivables",
      label: "Cartera",
      icon: "ri-hand-coin-line",
      hint: "Cuentas por cobrar (fiados) a clientes.",
      items: data.receivables.map((r) => ({
        id: r.id,
        title: r.clientName,
        subtitle: `${r.productName} · ${fmtDate(r.date)}`,
        meta: formatCOP(r.initialValue),
      })),
    },
    {
      key: "loans",
      label: "Préstamos",
      icon: "ri-exchange-dollar-line",
      hint: "Préstamos y devoluciones de dinero registrados.",
      items: data.loans.map((l) => ({
        id: l.id,
        title: l.concept,
        subtitle: `${l.type} · ${fmtDate(l.date)}`,
        meta: formatCOP(l.value),
      })),
    },
    {
      key: "promotions",
      label: "Promociones",
      icon: "ri-percent-line",
      hint: "Promociones y descuentos activos o pasados.",
      items: data.promotions.map((p) => ({
        id: p.id,
        title: p.title,
        subtitle: `${p.startDate || "—"} → ${p.endDate || "—"}`,
        meta: `-${p.discountPct}%`,
      })),
    },
    {
      key: "encargos",
      label: "Encargos",
      icon: "ri-file-list-3-line",
      hint: "Encargos / pedidos hechos por asesores.",
      items: data.encargos.map((e) => ({
        id: e.id,
        title: e.productName,
        subtitle: `${e.advisorName || "Asesor"} · ${e.qty} und`,
        meta: e.status,
      })),
    },
    {
      key: "movements",
      label: "Mov. asesor",
      icon: "ri-swap-box-line",
      hint: "Movimientos de cuenta de asesores (consignación, compra, pago, devolución).",
      items: data.advisorMovements.map((m) => ({
        id: m.id,
        title: m.productName,
        subtitle: `${m.kind} · ${fmtDate(m.date)} · ${m.qty} und`,
        meta: formatCOP(m.amount),
      })),
    },
    {
      key: "restock",
      label: "Reposiciones",
      icon: "ri-refresh-line",
      hint: "Solicitudes de reposición de inventario.",
      items: data.restockItems.map((i) => ({
        id: i.id,
        title: i.productName,
        subtitle: `${i.qty} und${i.note ? ` · ${i.note}` : ""}`,
        meta: fmtDate(i.createdAt),
      })),
    },
    {
      key: "stockEntries",
      label: "Puntos (PV)",
      icon: "ri-star-line",
      hint: "Registros de PV por compras de stock. Al borrarlos se quitan esos puntos de la calificación Amway.",
      items: (data.stockEntries ?? []).map((se) => ({
        id: se.id,
        title: se.productName,
        subtitle: `${se.source || "compra"} · ${fmtDate(se.date)} · ${se.qty} und`,
        meta: `PV ${se.pvTotal}`,
      })),
    },
  ];
};